/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { AttendanceGameHeader } from '@sportninja/common/features/game/attendance/utils/attendanceGameHeader'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import JerseyPattern from '@sportninja/common/components/JerseyPatterns'
import TeamsContainer from 'src/components/GameAttendanceCard/components/TeamsContainer'
import Text from 'src/components/ts-components/Text'
import type { AttendanceJerseys } from '../hooks/useAttendanceJerseys'

export interface AttendanceGameCardProps {
  header: AttendanceGameHeader
  /** Each team's game jersey, shown beside its logo. */
  jerseys: AttendanceJerseys
}

// Sized to sit level with the 32px team logo.
const JERSEY_WIDTH = 36
const JERSEY_HEIGHT = 32

const renderJersey = (pattern: AttendanceJerseys['pattern'], color: string) => (
  <span css={jerseyStyle} title={capitalizeWords('Game jersey')}>
    <JerseyPattern
      pattern={pattern}
      color={color}
      width={JERSEY_WIDTH}
      height={JERSEY_HEIGHT}
    />
  </span>
)

/**
 * The game this attendance is for, beside the summary counts: start time
 * (venue zone) over the home vs visitor matchup — the same matchup as the Home
 * sidebar's game card. The page frames it; this only lays out its contents.
 */
const AttendanceGameCard: React.FC<AttendanceGameCardProps> = ({
  header,
  jerseys,
}) => (
  <div css={cardStyle}>
    {header.dateLabel ? (
      <div css={dateRowStyle}>
        <Text
          variant='captionSmall'
          weight='semiBold'
          color={colors.NEUTRAL_50}
          style='margin: 0; letter-spacing: 1.44px; text-transform: uppercase;'
        >
          {header.dateLabel}
        </Text>
      </div>
    ) : null}
    <TeamsContainer
      css={matchupStyle}
      homeTeam={{
        name: header.homeTeam.name ?? '',
        logo: resolveAssetUrl(header.homeTeam.image) ?? '',
        jersey: renderJersey(jerseys.pattern, jerseys.homeColor),
      }}
      awayTeam={{
        name: header.visitingTeam.name ?? '',
        logo: resolveAssetUrl(header.visitingTeam.image) ?? '',
        jersey: renderJersey(jerseys.pattern, jerseys.visitorColor),
      }}
    />
  </div>
)

export default AttendanceGameCard

// ---- styled ----
const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const jerseyStyle = css`
  display: inline-flex;
  line-height: 0;
`

const dateRowStyle = css`
  display: flex;
  justify-content: center;
  padding-bottom: 12px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
`

// The sidebar card spaces the matchup from the content below it; here the
// card's own padding does that, and the matchup stays a readable width.
const matchupStyle = css`
  width: 100%;
  max-width: 480px;
  height: auto;
  margin: 0 auto;
`
